// v3 use case catalog (ADR 0002: the use case is the sized unit; spec "Domain model").
//
// Pure data, alongside the v2 lever catalog (data/leading-indicators.ts) until the UI switches
// over. Nineteen grid entries under the Customer Journey Alt 2 names and grid positions, plus two
// Efficiency entries. Retention is out of scope. Twelve are sized; the other nine are shown on the
// grid but disabled ("Not sized yet").
//
// Sources: decision log Q26-Q28, Q32-Q33, Q39-Q52 (formulas, defaults, confidence), spec table for
// attribution, and docs/v3-sfdc-admin-request.md for the metric keys — a test pins the two together.
//
// Percent-kind answers are stored as whole percent numbers (82 means 82%), as in v2.

import type { QuestionKind } from '../types';

export type MaturityTier = 'Crawl' | 'Walk' | 'Run' | 'Efficiency';
export type GridRow = 'Data foundation' | 'Grow pipeline' | 'Win deals' | 'Efficiency';
export type RecordLevel = 'accounts' | 'contacts';
/** One way of sizing the same outcome (GTM automation's time-savings methods). Methods add. */
export interface CalcMethod {
  id: string;
  /** The picker's label. */
  label: string;
  /** The section heading above that method's formula, when two or more are on. */
  heading: string;
  /** One line under the label on the page's first-visit entry cards. */
  description?: string;
}
/** The one path a use case takes into its lagging outcome (Q23). */
export type LaggingPath = 'pipeline' | 'win-rate' | 'cost';
/** The two lagging outcomes: revenue (via pipeline or win rate) and GTM cost saved. */
export type LaggingOutcome = 'revenue' | 'cost';
export type Measurement = 'telemetry-capable' | 'customer-reported';
export type QuestionRole = 'scaling' | 'current' | 'with-clay';
export type UseCaseConfidence = 'Benchmarked' | 'Partial';

export interface UseCaseQuestion {
  key: string;
  label: string;
  role: QuestionRole;
  def: number;
  kind: QuestionKind;
  /** Only asked (and counted) when this record level is on. Absent = always asked. */
  recordLevel?: RecordLevel;
  /** Only asked (and counted) when this calculation method is on. Absent = always asked. */
  method?: string;
  /** The default is the deal's ACV rather than `def` (PLG expansion's own ACV, Q47). */
  defaultFromDealAcv?: boolean;
}

/** Where a leading metric's today / with-Clay value comes from in the resolved answers. */
export type MetricSource =
  | { kind: 'question'; key: string }
  /** The base value lifted by a relative-lift question: base × (1 + lift%). */
  | { kind: 'lifted'; base: MetricSource; liftKey: string }
  | { kind: 'deal-win-rate' }
  | { kind: 'constant'; value: number }
  | { kind: 'tools-displaced' }
  /** A named sub-total of the use case's own formula (GTM automation's total hours saved). */
  | { kind: 'sub-total'; key: string };

export interface LeadingMetric {
  /** `uc-<use-case>[-<record-level or metric>]` — the Metric_Definition__c key. */
  key: string;
  name: string;
  unit: string;
  measurement: Measurement;
  recordLevel?: RecordLevel;
  /** Only reported when this calculation method is on. Absent = always reported. */
  method?: string;
  today: MetricSource;
  /** null for metrics with no today / with-Clay split (accounts enrolled, tools displaced). */
  withClay: MetricSource | null;
}

export interface Factor {
  pct: number;
  rationale: string;
}

export interface UseCaseOverlap {
  with: string;
  note: string;
}

interface UseCaseBase {
  id: string;
  name: string;
  tier: MaturityTier;
  gridRow: GridRow;
}

export interface UnsizedUseCase extends UseCaseBase {
  sized: false;
}

export interface SizedUseCase extends UseCaseBase {
  sized: true;
  confidence: UseCaseConfidence;
  outcome: LaggingOutcome;
  path: LaggingPath;
  /** Record levels the use case offers; the first is preselected. Empty = not a record-level use case. */
  recordLevels: RecordLevel[];
  /** The verb in the record-level picker's question: "Which type of records are being <verb>?". */
  levelVerb?: 'enriched' | 'sourced';
  /**
   * Authored copy shown under the record-level picker (what "accounts" vs "contacts" means for this
   * use case): one line per entry, its `term` in bold.
   */
  levelNote?: { term: string; text: string }[];
  /** One line per choice on the page's first-visit entry cards (Accounts / Contacts / Both). */
  levelChoiceNote?: Record<'accounts' | 'contacts' | 'both', string>;
  /**
   * Calculation methods the use case offers, in picker order; the first is preselected and at least
   * one stays on. Empty = one fixed formula. Selected methods' hours add (GTM automation).
   */
  methods: CalcMethod[];
  /** The win-rate path of Account / lead scoring reads the deal's current annual pipeline. */
  requiresCurrentAnnualPipeline: boolean;
  leadingMetrics: LeadingMetric[];
  questions: UseCaseQuestion[];
  realization: Factor;
  attribution: Factor;
  /** DRAFT claims, authored by Claude for Addison's review in the PR (Q53). */
  benchmarkClaim: { text: string; draft: boolean };
  overlaps: UseCaseOverlap[];
}

export type UseCase = SizedUseCase | UnsizedUseCase;

/* ------------------------------------------------------------------ shared pieces */

/**
 * Deal economics defaults. ACV and win rate are v2's company-profile defaults
 * (engine/share.ts defProfile: $40,000 / 25%). The contract estimate is blank by default (Q5).
 * Current annual pipeline defaults to $80M: it is only asked (and only counts) when a selected use
 * case reads it (Account / lead scoring), and like ACV and win rate it is "Assumed" until the SE
 * types their own, so a story built on defaults never sizes that use case at $0.
 */
export const DEFAULT_ACV = 40000;
export const DEFAULT_WIN_RATE = 25;
export const DEFAULT_CURRENT_ANNUAL_PIPELINE = 80_000_000;

const REALIZATION_BY_TIER: Record<MaturityTier, Factor> = {
  Crawl: { pct: 75, rationale: 'Crawl data work lands reliably, though not every enriched record gets worked.' },
  Walk: { pct: 70, rationale: 'Walk motions need your reps to adopt them and your process to change, so a little less lands.' },
  Run: { pct: 65, rationale: 'Run motions depend on several of your systems and teams working together.' },
  Efficiency: { pct: 80, rationale: 'Savings are contractual or hours-based, so most is realized once cancelled or automated.' },
};

const MEETING_TO_OPP = 30;

const q = (
  key: string,
  label: string,
  role: QuestionRole,
  def: number,
  kind: QuestionKind,
  extra: Partial<UseCaseQuestion> = {},
): UseCaseQuestion => ({ key, label, role, def, kind, ...extra });

const unsized = (id: string, name: string, tier: MaturityTier, gridRow: GridRow): UnsizedUseCase => ({
  id,
  name,
  tier,
  gridRow,
  sized: false,
});

/* ----------------------------------------------------------------------- the catalog */

const CRM_ENRICHMENT: SizedUseCase = {
  id: 'uc-crm-enrichment',
  name: 'CRM enrichment',
  tier: 'Crawl',
  gridRow: 'Data foundation',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: ['accounts', 'contacts'],
  levelVerb: 'enriched',
  levelNote: [
    {
      term: 'Account enrichment',
      text: 'usually means bringing the record up to an actionable level of data, e.g. enough to assign a territory or route it.',
    },
    { term: 'Contact enrichment', text: 'usually means finding validated, reachable contact information (email or phone).' },
  ],
  levelChoiceNote: {
    accounts: 'Bringing account records up to an actionable level of data, e.g. enough to assign a territory or route them.',
    contacts: 'Finding validated, reachable contact information (email or phone) for the people in your CRM.',
    // DRAFT copy for Addison's review.
    both: 'Sizes account and contact enrichment together; their pipeline adds.',
  },
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-crm-enrichment-accounts',
      name: 'CRM fill rate',
      unit: '% of account records',
      measurement: 'telemetry-capable',
      recordLevel: 'accounts',
      today: { kind: 'question', key: 'accounts_fill_current' },
      withClay: { kind: 'question', key: 'accounts_fill_clay' },
    },
    {
      key: 'uc-crm-enrichment-contacts',
      name: 'Contactable rate',
      unit: '% of contacts',
      measurement: 'telemetry-capable',
      recordLevel: 'contacts',
      today: { kind: 'question', key: 'contacts_reachable_current' },
      withClay: { kind: 'question', key: 'contacts_reachable_clay' },
    },
  ],
  questions: [
    q('accounts_records', 'Account records in your CRM', 'scaling', 100000, 'count', { recordLevel: 'accounts' }),
    q('accounts_fill_current', 'Current account fill rate', 'current', 40, 'pct', { recordLevel: 'accounts' }),
    q('accounts_fill_clay', 'Account fill rate with Clay', 'with-clay', 85, 'pct', { recordLevel: 'accounts' }),
    q('accounts_record_to_opp', 'Newly actionable records that become opportunities', 'scaling', 0.5, 'pct', { recordLevel: 'accounts' }),
    q('contacts_records', 'Contact records in your CRM', 'scaling', 500000, 'count', { recordLevel: 'contacts' }),
    q('contacts_reachable_current', 'Current contactable rate', 'current', 50, 'pct', { recordLevel: 'contacts' }),
    q('contacts_reachable_clay', 'Contactable rate with Clay', 'with-clay', 85, 'pct', { recordLevel: 'contacts' }),
    q('contacts_share_worked', 'Share of newly reachable contacts that your reps work', 'scaling', 25, 'pct', { recordLevel: 'contacts' }),
    q('contacts_to_meeting', 'Worked contacts that become meetings', 'scaling', 1, 'pct', { recordLevel: 'contacts' }),
    q('contacts_meeting_to_opp', 'Meetings that become opportunities', 'scaling', MEETING_TO_OPP, 'pct', { recordLevel: 'contacts' }),
  ],
  realization: REALIZATION_BY_TIER.Crawl,
  attribution: {
    pct: 25,
    rationale: 'Clay improves data your reps already work; your sales execution and other data sources share the credit.',
  },
  benchmarkClaim: {
    text: 'Fill to ~85% (Watershed 40% → 84%; Rillet 80–90%); Sana +60% data accuracy.',
    draft: true,
  },
  overlaps: [],
};

const ACCOUNT_LEAD_SCORING: SizedUseCase = {
  id: 'uc-account-lead-scoring',
  name: 'Account / lead scoring',
  tier: 'Crawl',
  gridRow: 'Data foundation',
  sized: true,
  confidence: 'Partial',
  outcome: 'revenue',
  path: 'win-rate',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: true,
  leadingMetrics: [
    {
      key: 'uc-account-lead-scoring',
      name: 'Scoring coverage',
      unit: '% of accounts/leads',
      measurement: 'telemetry-capable',
      today: { kind: 'question', key: 'coverage_current' },
      withClay: { kind: 'question', key: 'coverage_clay' },
    },
  ],
  questions: [
    q('coverage_current', 'Share of accounts and leads scored today', 'current', 30, 'pct'),
    q('coverage_clay', 'Share scored with Clay', 'with-clay', 100, 'pct'),
    q('winrate_lift', 'Relative win-rate lift on newly scored pipeline', 'with-clay', 10, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Crawl,
  attribution: {
    pct: 25,
    rationale: 'Clay supplies the scores; your reps and process decide whether scored accounts get worked first.',
  },
  benchmarkClaim: {
    text: 'Scoring the full pipeline against the ICP focuses your reps’ time on the accounts most likely to close.',
    draft: true,
  },
  overlaps: [],
};

const TAM_SOURCING: SizedUseCase = {
  id: 'uc-tam-sourcing',
  name: 'TAM sourcing',
  tier: 'Crawl',
  gridRow: 'Data foundation',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: ['accounts', 'contacts'],
  levelVerb: 'sourced',
  // DRAFT copy for Addison's review.
  levelChoiceNote: {
    accounts: 'Finding net-new accounts that match your ICP and are not in your CRM yet.',
    contacts: 'Finding net-new buyers at your target accounts, with reachable contact information.',
    both: 'Sizes account and contact sourcing together; their pipeline adds.',
  },
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-tam-sourcing-accounts',
      name: 'Net-new ICP accounts sourced',
      unit: 'accounts / year',
      measurement: 'telemetry-capable',
      recordLevel: 'accounts',
      today: { kind: 'question', key: 'accounts_year_today' },
      withClay: { kind: 'question', key: 'accounts_year_clay' },
    },
    {
      key: 'uc-tam-sourcing-contacts',
      name: 'Net-new ICP contacts sourced',
      unit: 'contacts / year',
      measurement: 'telemetry-capable',
      recordLevel: 'contacts',
      today: { kind: 'question', key: 'contacts_year_today' },
      withClay: { kind: 'question', key: 'contacts_year_clay' },
    },
  ],
  questions: [
    q('accounts_year_today', 'Net-new accounts sourced per year today', 'current', 1000, 'count', { recordLevel: 'accounts' }),
    q('accounts_year_clay', 'Net-new accounts sourced per year with Clay', 'with-clay', 10000, 'count', { recordLevel: 'accounts' }),
    q('accounts_to_opp', 'Sourced accounts that become opportunities', 'scaling', 2, 'pct', { recordLevel: 'accounts' }),
    q('contacts_year_today', 'Net-new contacts sourced per year today', 'current', 2000, 'count', { recordLevel: 'contacts' }),
    q('contacts_year_clay', 'Net-new contacts sourced per year with Clay', 'with-clay', 24000, 'count', { recordLevel: 'contacts' }),
    q('contacts_to_meeting', 'Sourced contacts that become meetings', 'scaling', 1, 'pct', { recordLevel: 'contacts' }),
    q('contacts_meeting_to_opp', 'Meetings that become opportunities', 'scaling', MEETING_TO_OPP, 'pct', { recordLevel: 'contacts' }),
  ],
  realization: REALIZATION_BY_TIER.Crawl,
  attribution: {
    pct: 50,
    rationale: 'Clay produces the net-new accounts and contacts outright, but your sales team still has to convert them.',
  },
  benchmarkClaim: {
    text: '4× faster list building. Mistral sourced 25K accounts in 2 weeks; A-Lign hit a 59% win rate on Clay-built lists.',
    draft: true,
  },
  overlaps: [],
};

const INBOUND_SPEED_TO_LEAD: SizedUseCase = {
  id: 'uc-inbound-speed-to-lead',
  name: 'Inbound speed-to-lead',
  tier: 'Walk',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Partial',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-inbound-speed-to-lead-match',
      name: 'Inbound match rate',
      unit: '% of inbound leads',
      measurement: 'telemetry-capable',
      today: { kind: 'question', key: 'match_current' },
      withClay: { kind: 'question', key: 'match_clay' },
    },
    {
      key: 'uc-inbound-speed-to-lead-conversion',
      name: 'Inbound lead-to-opp conversion',
      unit: '%',
      measurement: 'customer-reported',
      today: { kind: 'question', key: 'conversion_current' },
      withClay: { kind: 'lifted', base: { kind: 'question', key: 'conversion_current' }, liftKey: 'speed_lift' },
    },
  ],
  questions: [
    q('leads_year', 'Inbound leads per year', 'scaling', 6000, 'count'),
    q('qualified_pct', 'Leads that are qualified', 'scaling', 50, 'pct'),
    q('match_current', 'Current inbound match rate', 'current', 40, 'pct'),
    q('match_clay', 'Inbound match rate with Clay', 'with-clay', 85, 'pct'),
    q('conversion_current', 'Current lead-to-opportunity conversion', 'current', 8, 'pct'),
    q('speed_lift', 'Relative conversion lift from speed-to-lead', 'with-clay', 25, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Walk,
  attribution: {
    pct: 30,
    rationale: 'Clay speeds up and enriches the lead; your routing, SDR follow-up and offer drive the conversion.',
  },
  benchmarkClaim: {
    text: 'Match rate to ~85% (OpenAI 40% → 80%+); responding in minutes rather than an hour sharply lifts qualification.',
    draft: true,
  },
  overlaps: [],
};

const AUTOMATED_OUTBOUND: SizedUseCase = {
  id: 'uc-automated-outbound',
  name: 'Automated outbound',
  tier: 'Walk',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-automated-outbound',
      name: 'Automated outreach sent',
      unit: 'contacts / year',
      measurement: 'telemetry-capable',
      // Automated outbound is a new motion: nothing is sequenced through it before Clay (no question).
      today: { kind: 'constant', value: 0 },
      withClay: { kind: 'question', key: 'outreach_year' },
    },
  ],
  questions: [
    // 36,000 = the old default chain (5 SDRs × (900 − 300) contacts a month × 12), so the volume is unchanged.
    q('outreach_year', 'Contacts sequenced per year through automated outbound', 'with-clay', 36000, 'count'),
    // 1%: BetterHelp booked 3 calls from 400–500 sequenced leads (0.6–0.75%) and called it strong.
    q('meeting_rate', 'Meeting rate on sequenced contacts', 'scaling', 1, 'pct'),
    q('meeting_to_opp', 'Meetings that become opportunities', 'scaling', MEETING_TO_OPP, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Walk,
  attribution: {
    pct: 50,
    rationale: 'Clay produces the outbound volume; your messaging and sequencing share the result.',
  },
  benchmarkClaim: {
    text: 'Up to 3× output at the same headcount. Intercom +140% pipeline; Recharge 8× volume.',
    draft: true,
  },
  overlaps: [
    {
      with: 'uc-rep-prospecting',
      note: 'Automated outbound and Rep prospecting both add prospecting volume, so some of the same meetings are counted twice. Lower realization if your teams overlap.',
    },
    {
      with: 'uc-signal-based-outbound',
      note: 'Automated outbound and Signal-based outbound reach some of the same contacts, so some of the same meetings are counted twice. Lower realization if your programs overlap.',
    },
  ],
};

const PERFORMANCE_MARKETING: SizedUseCase = {
  id: 'uc-performance-marketing',
  name: 'Performance marketing',
  tier: 'Walk',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Partial',
  outcome: 'cost',
  path: 'cost',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-performance-marketing',
      name: 'Ad audience match rate',
      unit: '%',
      measurement: 'customer-reported',
      today: { kind: 'question', key: 'match_current' },
      withClay: { kind: 'question', key: 'match_clay' },
    },
  ],
  questions: [
    q('ad_spend_year', 'Your paid-ad spend per year', 'scaling', 240000, 'money'),
    q('match_current', 'Current audience match rate', 'current', 50, 'pct'),
    q('match_clay', 'Audience match rate with Clay', 'with-clay', 90, 'pct'),
    q('recoverable_pct', 'Share of wasted spend that is recoverable', 'with-clay', 50, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Walk,
  attribution: {
    pct: 60,
    rationale: 'Clay supplies the matched audiences; your ad platform and creative share the result.',
  },
  benchmarkClaim: {
    text: 'Syncing enriched, ICP-matched audiences cuts the spend wasted on the wrong accounts.',
    draft: true,
  },
  overlaps: [],
};

const REP_PROSPECTING: SizedUseCase = {
  id: 'uc-rep-prospecting',
  name: 'Rep prospecting',
  tier: 'Walk',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Partial',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-rep-prospecting',
      name: 'Prospects added per rep',
      unit: 'prospects / rep / week',
      measurement: 'telemetry-capable',
      today: { kind: 'question', key: 'prospects_week_today' },
      withClay: { kind: 'question', key: 'prospects_week_clay' },
    },
  ],
  questions: [
    q('reps', 'Reps prospecting on your team', 'scaling', 10, 'count'),
    q('prospects_week_today', 'Prospects added per rep per week today', 'current', 20, 'count'),
    q('prospects_week_clay', 'Prospects added per rep per week with Clay', 'with-clay', 35, 'count'),
    q('meeting_rate', 'Meeting rate on prospects', 'scaling', 3, 'pct'),
    q('meeting_to_opp', 'Meetings that become opportunities', 'scaling', MEETING_TO_OPP, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Walk,
  attribution: {
    pct: 30,
    rationale: 'Clay improves a human activity: your reps still choose and work the prospects.',
  },
  benchmarkClaim: {
    text: 'Your reps add more researched, enriched prospects each week with Clay, without adding hours.',
    draft: true,
  },
  overlaps: [
    {
      with: 'uc-automated-outbound',
      note: 'Rep prospecting and Automated outbound both add prospecting volume, so some of the same meetings are counted twice. Lower realization if your teams overlap.',
    },
  ],
};

const SIGNAL_BASED_OUTBOUND: SizedUseCase = {
  id: 'uc-signal-based-outbound',
  name: 'Signal-based outbound',
  tier: 'Run',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-signal-based-outbound',
      name: 'Signal-outreach meeting rate',
      unit: '%',
      measurement: 'customer-reported',
      today: { kind: 'question', key: 'meeting_rate_today' },
      withClay: { kind: 'question', key: 'meeting_rate_clay' },
    },
  ],
  questions: [
    q('signal_contacts_month', 'Signal-triggered contacts per month', 'scaling', 1000, 'count'),
    q('meeting_rate_today', 'Meeting rate on signal outreach today', 'current', 2, 'pct'),
    q('meeting_rate_clay', 'Meeting rate on signal outreach with Clay', 'with-clay', 3, 'pct'),
    q('meeting_to_opp', 'Meetings that become opportunities', 'scaling', MEETING_TO_OPP, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Run,
  attribution: {
    pct: 40,
    rationale: 'Clay detects the signals, but your reps’ timing and message turn them into meetings.',
  },
  benchmarkClaim: {
    text: '~2× reply / win-rate lift (Hex +50% win rate, Verkada 100% reply lift).',
    draft: true,
  },
  overlaps: [
    {
      with: 'uc-automated-outbound',
      note: 'Signal-based outbound and Automated outbound reach some of the same contacts, so some of the same meetings are counted twice. Lower realization if your programs overlap.',
    },
  ],
};

const PLG_EXPANSION: SizedUseCase = {
  id: 'uc-plg-expansion',
  name: 'PLG expansion',
  tier: 'Run',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Partial',
  outcome: 'revenue',
  path: 'pipeline',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-plg-expansion',
      name: 'PQL-to-opportunity conversion',
      unit: '%',
      measurement: 'customer-reported',
      today: { kind: 'question', key: 'conversion_today' },
      withClay: { kind: 'question', key: 'conversion_clay' },
    },
  ],
  questions: [
    q('signups_month', 'Your product signups per month', 'scaling', 2000, 'count'),
    q('icp_fit', 'Signups that fit the ICP', 'scaling', 20, 'pct'),
    q('plg_acv', 'Average deal size for self-serve upgrades (PLG ACV)', 'scaling', 0, 'money', { defaultFromDealAcv: true }),
    q('conversion_today', 'ICP-fit signups that become opportunities today', 'current', 3, 'pct'),
    q('conversion_clay', 'ICP-fit signups that become opportunities with Clay', 'with-clay', 3.9, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Run,
  attribution: {
    pct: 40,
    rationale: 'Clay qualifies and routes signups; your product experience drives much of the conversion.',
  },
  benchmarkClaim: {
    text: 'Enriching and routing product signups turns more ICP-fit users into sales conversations.',
    draft: true,
  },
  overlaps: [],
};

const ABM: SizedUseCase = {
  id: 'uc-abm',
  name: 'Account-based marketing',
  tier: 'Run',
  gridRow: 'Grow pipeline',
  sized: true,
  confidence: 'Partial',
  outcome: 'revenue',
  path: 'win-rate',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-abm-accounts-enrolled',
      name: 'Accounts enrolled in ABM',
      unit: 'accounts',
      measurement: 'telemetry-capable',
      today: { kind: 'question', key: 'accounts_enrolled' },
      withClay: null,
    },
    {
      key: 'uc-abm-win-rate',
      name: 'Win rate on ABM accounts',
      unit: '%',
      measurement: 'customer-reported',
      today: { kind: 'deal-win-rate' },
      withClay: { kind: 'lifted', base: { kind: 'deal-win-rate' }, liftKey: 'winrate_uplift' },
    },
  ],
  questions: [
    q('accounts_enrolled', 'Accounts enrolled in your ABM program', 'scaling', 300, 'count'),
    q('open_opp_pct', 'Enrolled accounts with an open opportunity each year', 'scaling', 25, 'pct'),
    q('winrate_uplift', 'Relative win-rate uplift from ABM', 'with-clay', 15, 'pct'),
  ],
  realization: REALIZATION_BY_TIER.Run,
  attribution: {
    pct: 30,
    rationale: 'Clay improves a human-run program; your marketing and sales execution share the uplift.',
  },
  benchmarkClaim: {
    text: '20% rate lift from better targeting and personalization at scale. Hex +50% win rate.',
    draft: true,
  },
  overlaps: [],
};

const TOOL_CONSOLIDATION: SizedUseCase = {
  id: 'uc-tool-consolidation',
  name: 'Tool consolidation',
  tier: 'Efficiency',
  gridRow: 'Efficiency',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'cost',
  path: 'cost',
  recordLevels: [],
  methods: [],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-tool-consolidation',
      name: 'Tools displaced',
      unit: 'tools',
      measurement: 'customer-reported',
      today: { kind: 'tools-displaced' },
      withClay: null,
    },
  ],
  // No questions: the input is the displaced-vendor checklist (engine input `tools`).
  questions: [],
  realization: REALIZATION_BY_TIER.Efficiency,
  attribution: {
    pct: 100,
    rationale: 'The displaced vendor contracts are cancelled outright, so there is no other system to share credit with.',
  },
  benchmarkClaim: {
    text: '~40% stack-cost reduction (Samsara 91–96%). A-Lign replaced a $60K contract and got 15× more data.',
    draft: true,
  },
  overlaps: [],
};

const GTM_AUTOMATION: SizedUseCase = {
  id: 'uc-gtm-automation',
  name: 'GTM automation',
  tier: 'Efficiency',
  gridRow: 'Efficiency',
  sized: true,
  confidence: 'Benchmarked',
  outcome: 'cost',
  path: 'cost',
  recordLevels: [],
  // Three ways to size the time Clay saves; any mix. Hours add, then the hourly rate applies once.
  // DRAFT descriptions for Addison's review.
  methods: [
    {
      id: 'share',
      label: 'Work hours × automation',
      heading: 'Method A: work hours × automation share',
      description: 'Your team’s weekly hours on manual GTM tasks, times the share of that work Clay automates.',
    },
    {
      id: 'flat',
      label: 'Flat hours saved',
      heading: 'Method B: flat hours saved',
      description: 'A flat number of hours Clay saves your team each week, when the customer already has one.',
    },
    {
      id: 'records',
      label: 'Minutes saved per record',
      heading: 'Method C: minutes saved per record × records worked',
      description: 'Minutes Clay saves on each record, times the records your team works each week.',
    },
  ],
  requiresCurrentAnnualPipeline: false,
  leadingMetrics: [
    {
      key: 'uc-gtm-automation',
      name: 'Share of manual GTM hours automated',
      unit: '%',
      measurement: 'customer-reported',
      method: 'share',
      // Nothing is automated before Clay, so today's level is zero by definition (no question).
      today: { kind: 'constant', value: 0 },
      withClay: { kind: 'question', key: 'automation_pct' },
    },
    {
      key: 'uc-gtm-automation-hours',
      name: 'Hours saved per year',
      unit: 'hours / year',
      measurement: 'customer-reported',
      // Covers every selected method; nothing is saved before Clay.
      today: { kind: 'constant', value: 0 },
      withClay: { kind: 'sub-total', key: 'hours_year' },
    },
  ],
  questions: [
    q('annual_cost', 'Fully-loaded sales personnel cost per year', 'scaling', 95000, 'money'),
    q('hours_week', 'Your team hours per week on manual GTM tasks', 'current', 60, 'hrs', { method: 'share' }),
    q('automation_pct', 'Share of that work Clay can automate', 'with-clay', 75, 'pct', { method: 'share' }),
    // DRAFT default for review (round 12): 10 hrs a week.
    q('hours_saved_week', 'Hours Clay saves your team per week (flat)', 'with-clay', 10, 'hrs', { method: 'flat' }),
    // 10 min: Deltek cut lead research from 15 to ≤3 min; rounded down as not every record needs full research.
    q('minutes_per_record', 'Minutes Clay saves per record', 'with-clay', 10, 'min', { method: 'records' }),
    q('records_week', 'Records your team works per week', 'scaling', 200, 'count', { method: 'records' }),
  ],
  realization: REALIZATION_BY_TIER.Efficiency,
  attribution: {
    pct: 100,
    rationale: 'The automation-share question already scopes the claim to Clay’s own work.',
  },
  benchmarkClaim: {
    text: '75% of manual GTM work automated (Pump). Anthropic saves 10+ hrs/week per rep.',
    draft: true,
  },
  overlaps: [],
};

/**
 * The master list in adoption order: Crawl, Walk, Run, then Efficiency. Within a tier, grid reading
 * order (Data foundation, Grow pipeline, Win deals). This is also the use case page order (story 26).
 */
export const USE_CASES: readonly UseCase[] = [
  // Crawl
  CRM_ENRICHMENT,
  ACCOUNT_LEAD_SCORING,
  TAM_SOURCING,
  unsized('uc-build-prospecting-lists', 'Build prospecting lists', 'Crawl', 'Data foundation'),
  // Walk
  unsized('uc-territory-planning', 'Territory planning', 'Walk', 'Data foundation'),
  unsized('uc-account-intelligence', 'Account intelligence', 'Walk', 'Data foundation'),
  INBOUND_SPEED_TO_LEAD,
  AUTOMATED_OUTBOUND,
  PERFORMANCE_MARKETING,
  REP_PROSPECTING,
  unsized('uc-pre-call-prep', 'Pre-call prep', 'Walk', 'Win deals'),
  unsized('uc-meeting-follow-up', 'Meeting follow-up', 'Walk', 'Win deals'),
  // Run
  SIGNAL_BASED_OUTBOUND,
  PLG_EXPANSION,
  ABM,
  unsized('uc-field-marketing', 'Field marketing', 'Run', 'Grow pipeline'),
  unsized('uc-warm-lead-nurture', 'Warm lead nurture', 'Run', 'Grow pipeline'),
  unsized('uc-live-deal-alerts', 'Live deal alerts', 'Run', 'Win deals'),
  unsized('uc-personalized-asset-creation', 'Personalized asset creation', 'Run', 'Win deals'),
  // Efficiency (outside the grid)
  TOOL_CONSOLIDATION,
  GTM_AUTOMATION,
];

export const SIZED_USE_CASES: readonly SizedUseCase[] = USE_CASES.filter(
  (u): u is SizedUseCase => u.sized,
);

const BY_ID = new Map<string, UseCase>(USE_CASES.map((u) => [u.id, u]));

export function getUseCase(id: string): UseCase | undefined {
  return BY_ID.get(id);
}

export function getSizedUseCase(id: string): SizedUseCase | undefined {
  const u = BY_ID.get(id);
  return u && u.sized ? u : undefined;
}

/** Every `Metric_Definition__c` key the catalog produces, in catalog order. */
export function allMetricKeys(): string[] {
  return SIZED_USE_CASES.flatMap((u) => u.leadingMetrics.map((m) => m.key));
}
